import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Printer, FileText, Check, ArrowRight, Brain, User, FolderOpen, Loader2 } from 'lucide-react';

const EFAXSlide = () => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setStep((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="h-full w-full bg-slate-900 text-white flex items-center justify-center p-12 relative overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-green-600/10 rounded-full blur-[100px] -translate-y-1/2 -translate-x-1/2" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[100px] translate-y-1/2 translate-x-1/2" />

        <div className="flex flex-row items-center gap-16 max-w-7xl mx-auto z-10 w-full">
            
            {/* Left Content */}
            <div className="flex-1 space-y-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-900/30 text-green-400 text-sm font-bold border border-green-800">
                    <Printer className="w-4 h-4" />
                    <span>Intelligent EFAX</span>
                </div>

                <h2 className="text-4xl md:text-6xl font-bold leading-tight">
                    Faxes that <br />
                    <span className="text-green-400">File Themselves</span>
                </h2>

                <p className="text-xl text-slate-300 leading-relaxed max-w-xl">
                    Don't just receive faxes digitally. Our AI reads every incoming page, identifies the patient, and files it directly into the correct chart folder.
                </p>

                <div className="space-y-4 pt-4">
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                        <div className="flex items-start gap-4">
                            <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                                <Check className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="font-bold text-white text-lg">Included in Plan</h4>
                                <p className="text-slate-400 text-sm">No extra monthly fees. Unlimited pages.</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                        <div className="flex items-start gap-4">
                            <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400">
                                <Brain className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="font-bold text-white text-lg">AI Categorization</h4>
                                <p className="text-slate-400 text-sm">Distinguishes between EOBs, Referrals, and Lab Results automatically.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Visual - Process Animation */}
            <div className="flex-1 w-full max-w-lg">
                <div className="bg-slate-800 rounded-2xl border border-slate-700 p-8 relative overflow-hidden shadow-2xl">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 to-blue-500" />
                    
                    <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-8 text-center">Live Processing Pipeline</h3>

                    <div className="space-y-6 relative">
                        {/* Connecting Line */}
                        <div className="absolute left-6 top-6 bottom-6 w-0.5 bg-slate-700 -z-10" />

                        {/* Step 1: Receive */}
                        <div className={`flex items-center gap-4 transition-all duration-500 ${step >= 0 ? 'opacity-100' : 'opacity-30'}`}>
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${step === 0 ? 'bg-blue-500 border-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.5)]' : step > 0 ? 'bg-slate-700 border-green-500 text-green-500' : 'bg-slate-800 border-slate-600 text-slate-500'}`}>
                                {step > 0 ? <Check className="w-6 h-6" /> : <Printer className="w-6 h-6 text-white" />}
                            </div>
                            <div className="flex-1 bg-slate-700/50 p-3 rounded-lg border border-slate-600">
                                <div className="flex justify-between items-center">
                                    <span className="font-semibold text-white">Incoming Fax</span>
                                    <span className="text-xs text-slate-400">3 pages</span>
                                </div>
                                {step === 0 && (
                                    <motion.div layoutId="fax-doc" className="mt-2 h-16 bg-white rounded p-2 shadow-sm relative overflow-hidden">
                                        <div className="space-y-1">
                                            <div className="h-1.5 w-3/4 bg-slate-200 rounded" />
                                            <div className="h-1.5 w-1/2 bg-slate-200 rounded" />
                                            <div className="h-1.5 w-full bg-slate-200 rounded" />
                                        </div>
                                        <div className="absolute inset-0 bg-blue-500/10 animate-pulse" />
                                    </motion.div>
                                )}
                            </div>
                        </div>

                        {/* Step 2: Analyze */}
                        <div className={`flex items-center gap-4 transition-all duration-500 ${step >= 1 ? 'opacity-100' : 'opacity-30'}`}>
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${step === 1 ? 'bg-purple-500 border-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.5)]' : step > 1 ? 'bg-slate-700 border-green-500 text-green-500' : 'bg-slate-800 border-slate-600 text-slate-500'}`}>
                                {step === 1 ? <Loader2 className="w-6 h-6 text-white animate-spin" /> : step > 1 ? <Check className="w-6 h-6" /> : <Brain className="w-6 h-6 text-white" />}
                            </div>
                            <div className="flex-1 bg-slate-700/50 p-3 rounded-lg border border-slate-600">
                                <span className="font-semibold text-white block mb-1">AI Analysis</span>
                                {step === 1 && (
                                    <div className="text-xs text-purple-300 space-y-1">
                                        <motion.div initial={{ x: -10, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="flex items-center gap-2"><Check className="w-3 h-3" /> Identified: Sarah Miller</motion.div>
                                        <motion.div initial={{ x: -10, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.5 }} className="flex items-center gap-2"><Check className="w-3 h-3" /> Type: Specialist Referral</motion.div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Step 3: File */}
                        <div className={`flex items-center gap-4 transition-all duration-500 ${step >= 2 ? 'opacity-100' : 'opacity-30'}`}>
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${step === 2 ? 'bg-green-500 border-green-400 shadow-[0_0_15px_rgba(34,197,94,0.5)]' : step > 2 ? 'bg-slate-700 border-green-500 text-green-500' : 'bg-slate-800 border-slate-600 text-slate-500'}`}>
                                {step > 2 ? <Check className="w-6 h-6" /> : <FolderOpen className="w-6 h-6 text-white" />}
                            </div>
                            <div className="flex-1 bg-slate-700/50 p-3 rounded-lg border border-slate-600">
                                <span className="font-semibold text-white block">Filed to PMS</span>
                                {step >= 2 && (
                                    <motion.div 
                                        initial={{ scale: 0.9, opacity: 0 }} 
                                        animate={{ scale: 1, opacity: 1 }}
                                        className="mt-2 bg-slate-800 rounded p-2 border border-slate-600 flex items-center gap-2"
                                    >
                                        <User className="w-4 h-4 text-blue-400" />
                                        <span className="text-xs text-slate-300">Patients / Miller, Sarah / Referrals</span>
                                    </motion.div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </div>

        </div>
    </div>
  );
};

export default EFAXSlide;























